/**
 * Jobs Page
 *
 * Manage active, scheduled, and completed jobs.
 * PURELY PRESENTATIONAL - all data comes from hooks.
 *
 * DDD Compliance:
 * - Uses application service hooks for data (useJobList)
 * - No business logic or calculations in component
 * - Displays view models from application layer
 * - Handles loading, error, and empty states gracefully
 *
 * Stage 2 Enhancements:
 * - Card and list view modes
 * - Bulk operations (start, complete, cancel)
 * - CSV export
 * - Client-side sorting
 */

import { useState, useMemo } from 'react';
import { PageHeader } from '@/components/service-provider/PageHeader';
import { useJobList, useRescheduleJob } from '@/application/service-provider/hooks/useJobData';
import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';
import { JobDetailModal } from '@/components/service-provider/JobDetailModal';
import { ViewModeToggle, type ViewMode } from '@/components/service-provider/ViewModeToggle';
import { JobsListView } from '@/components/service-provider/JobsListView';
import { JobsCalendarView } from '@/components/service-provider/JobsCalendarView';
import { JobsMapView } from '@/components/service-provider/JobsMapView';
import { BulkActionsToolbar } from '@/components/service-provider/BulkActionsToolbar';
import { CreateJobButton } from '@/components/service-provider/CreateJobButton';
import { sortJobs, type SortOption } from '@/lib/jobSorting';
import { exportJobsToCSV } from '@/lib/csvExport';


export function JobsPage() {
  // UI state (not business logic)
  const [activeFilters, setActiveFilters] = useState<{
    status?: string;
    dateRange?: string;
    sortBy?: string;
  }>({});
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>();

  // Stage 2: View mode and selection state
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [clientSortBy, setClientSortBy] = useState<SortOption>('date-desc');

  // Fetch jobs with current filters
  const { data, isLoading, error } = useJobList(activeFilters);

  // Stage 3: Reschedule mutation for calendar drag-drop
  const rescheduleJob = useRescheduleJob();

  // Apply client-side sorting (Stage 2)
  const sortedJobs = useMemo(() => {
    if (!data?.jobs) return [];
    return sortJobs(data.jobs, clientSortBy);
  }, [data?.jobs, clientSortBy]);

  // Handler for filter changes
  const handleFilterChange = (filterType: 'status' | 'dateRange' | 'sortBy', value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Stage 2: Selection handlers
  const handleToggleSelection = (jobId: string) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    if (selectedJobIds.size === sortedJobs.length) {
      setSelectedJobIds(new Set());
    } else {
      setSelectedJobIds(new Set(sortedJobs.map(j => j.id)));
    }
  };

  const handleClearSelection = () => {
    setSelectedJobIds(new Set());
  };

  // Stage 3: Calendar reschedule handler
  const handleReschedule = async (jobId: string, newStartDate: Date) => {
    try {
      // Find the job to get its existing priority and timeWindow
      const job = sortedJobs.find(j => j.id === jobId);
      if (!job) {
        throw new Error('Job not found');
      }

      await rescheduleJob.mutateAsync({
        jobId,
        schedule: {
          scheduledDate: newStartDate.toISOString(),
          priority: job.priority, // Preserve existing priority
          ...(job.timeWindow && { timeWindow: job.timeWindow }), // Preserve timeWindow if it exists
        },
      });
    } catch (error) {
      // Error will be handled by the mutation's onError callback
      console.error('Failed to reschedule job:', error);
      throw error; // Re-throw to let the mutation handle it
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div>
        <PageHeader
          title="Jobs"
          description="Manage your current and upcoming job assignments"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Jobs' },
          ]}
        />
        <div className="space-y-6">
          <FiltersSkeleton />
          <JobListSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div>
        <PageHeader
          title="Jobs"
          description="Manage your current and upcoming job assignments"
          breadcrumbs={[
            { label: 'Dashboard', href: '/provider/dashboard' },
            { label: 'Jobs' },
          ]}
        />
        <ErrorState message={error?.message || 'Failed to load jobs'} />
      </div>
    );
  }

  const { filters, summary, isEmpty, emptyStateMessage } = data;

  return (
    <div>
      <PageHeader
        title="Jobs"
        description="Manage your current and upcoming job assignments"
        breadcrumbs={[
          { label: 'Dashboard', href: '/provider/dashboard' },
          { label: 'Jobs' },
        ]}
      />

      {/* Create Job Action */}
      <div className="px-4 md:px-6 lg:px-8 -mt-2 mb-4">
        <CreateJobButton />
      </div>

      <div className="space-y-6">
        {/* Filters */}
        <div className="card">
          <div className="card-content">
            {/* Status Filter Pills */}
            <div className="mb-4">
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Status
              </label>
              <div className="flex flex-wrap gap-2">
                {filters.statuses.map((status) => (
                  <FilterPill
                    key={status.value}
                    label={status.label}
                    count={status.count}
                    isActive={status.isActive}
                    onClick={() => handleFilterChange('status', status.value)}
                  />
                ))}
              </div>
            </div>

            {/* Sort and Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sort Dropdown */}
              <div>
                <label
                  htmlFor="sort"
                  className="text-sm font-medium text-neutral-700 mb-2 block"
                >
                  Sort By
                </label>
                <select
                  id="sort"
                  value={activeFilters.sortBy || 'date-desc'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {filters.sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range Dropdown */}
              <div>
                <label
                  htmlFor="dateRange"
                  className="text-sm font-medium text-neutral-700 mb-2 block"
                >
                  Date Range
                </label>
                <select
                  id="dateRange"
                  value={activeFilters.dateRange || 'all'}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {filters.dateRanges.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar: Summary, View Toggle, and Actions */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-neutral-600">{summary.displayText}</p>

          <div className="flex items-center gap-3">
            {/* Client-side Sort */}
            <select
              value={clientSortBy}
              onChange={(e) => setClientSortBy(e.target.value as SortOption)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="date-desc">Date (Newest First)</option>
              <option value="date-asc">Date (Oldest First)</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="status">Status</option>
            </select>

            {/* Export CSV Button */}
            <button
              onClick={() => exportJobsToCSV(sortedJobs)}
              className="btn-secondary text-sm"
              disabled={sortedJobs.length === 0}
            >
              Export All to CSV
            </button>

            {/* View Mode Toggle */}
            <ViewModeToggle mode={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {/* Bulk Actions Toolbar (appears when jobs selected) */}
        {selectedJobIds.size > 0 && (
          <BulkActionsToolbar
            selectedJobIds={selectedJobIds}
            allJobs={sortedJobs}
            onClearSelection={handleClearSelection}
          />
        )}

        {/* Job List or Empty State */}
        {isEmpty ? (
          <EmptyState message={emptyStateMessage} />
        ) : viewMode === 'calendar' ? (
          <JobsCalendarView
            jobs={sortedJobs}
            onReschedule={handleReschedule}
            onViewDetails={(jobId) => setSelectedJobId(jobId)}
          />
        ) : viewMode === 'map' ? (
          <JobsMapView
            jobs={sortedJobs}
            onViewDetails={(jobId) => setSelectedJobId(jobId)}
          />
        ) : viewMode === 'list' ? (
          <JobsListView
            jobs={sortedJobs}
            selectedJobIds={selectedJobIds}
            onToggleSelection={handleToggleSelection}
            onToggleAll={handleToggleAll}
            onViewDetails={(jobId) => setSelectedJobId(jobId)}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedJobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                onViewDetails={() => setSelectedJobId(job.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {selectedJobId && (
        <JobDetailModal
          jobId={selectedJobId}
          onClose={() => setSelectedJobId(undefined)}
        />
      )}


    </div>
  );
}

// Helper Components (purely presentational)

function FilterPill({
  label,
  count,
  isActive,
  onClick,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
        isActive
          ? 'bg-primary-500 text-white'
          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
      }`}
    >
      {label} ({count})
    </button>
  );
}

function JobCard({
  job,
  onViewDetails,
}: {
  job: JobCardViewModel;
  onViewDetails: () => void;
}) {
  return (
    <div className="card hover:shadow-lg transition-shadow">
      <div className="card-content">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-neutral-900 mb-1">
              {job.displayName}
            </h3>
            <p className="text-sm text-neutral-600">{job.displayAddress}</p>
          </div>
          <StatusBadge badge={job.statusBadge} />
        </div>

        {/* Schedule */}
        <div className="mb-3">
          <p className="text-sm text-neutral-700">
            <span className="font-medium">Schedule:</span> {job.scheduleText}
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-neutral-50 rounded p-2">
            <p className="text-xs text-neutral-600">Project Type</p>
            <p className="text-sm font-medium text-neutral-900">{job.projectType || 'N/A'}</p>
          </div>
          <div className="bg-neutral-50 rounded p-2">
            <p className="text-xs text-neutral-600">Est. Waste</p>
            <p className="text-sm font-medium text-neutral-900">{job.estimatedWaste || 'N/A'}</p>
          </div>
        </div>

        {/* Urgency Indicator */}
        {job.urgencyLevel !== 'normal' && (
          <div
            className={`mb-3 p-2 rounded ${
              job.urgencyLevel === 'urgent'
                ? 'bg-red-50 border border-red-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}
          >
            <p
              className={`text-xs font-medium ${
                job.urgencyLevel === 'urgent' ? 'text-red-700' : 'text-yellow-700'
              }`}
            >
              {job.urgencyLevel === 'urgent'
                ? 'Overdue - Needs attention'
                : 'Due soon'}
              {job.daysRemaining !== undefined &&
                ` (${Math.abs(job.daysRemaining)} days ${job.daysRemaining < 0 ? 'overdue' : 'remaining'})`}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onViewDetails}
            className="flex-1 btn-primary text-sm"
            disabled={!job.canViewDetails}
          >
            View Details
          </button>

        </div>
      </div>
    </div>
  );
}

function StatusBadge({
  badge,
}: {
  badge: { label: string; color: string; icon: string };
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  }[badge.color] || 'bg-gray-100 text-gray-800';

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-medium ${colorClasses}`}
    >
      {badge.label}
    </span>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="card">
      <div className="card-content text-center py-12">
        <svg
          className="w-16 h-16 text-neutral-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
        <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Jobs Found</h3>
        <p className="text-neutral-600 mb-4">{message}</p>
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="card">
      <div className="card-content">
        <div className="text-center py-8">
          <div className="text-red-600 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            Unable to Load Jobs
          </h3>
          <p className="text-sm text-neutral-600 mb-4">{message}</p>
          <button
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  );
}

function FiltersSkeleton() {
  return (
    <div className="card">
      <div className="card-content animate-pulse">
        <div className="mb-4">
          <div className="h-4 bg-neutral-200 rounded w-16 mb-2"></div>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 w-24 bg-neutral-200 rounded-full"></div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="h-4 bg-neutral-200 rounded w-16 mb-2"></div>
            <div className="h-10 bg-neutral-200 rounded"></div>
          </div>
          <div>
            <div className="h-4 bg-neutral-200 rounded w-20 mb-2"></div>
            <div className="h-10 bg-neutral-200 rounded"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function JobListSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="card">
          <div className="card-content">
            <div className="flex justify-between mb-3">
              <div className="flex-1">
                <div className="h-6 bg-neutral-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-neutral-200 rounded w-full"></div>
              </div>
              <div className="h-6 w-20 bg-neutral-200 rounded-full"></div>
            </div>
            <div className="h-4 bg-neutral-200 rounded w-2/3 mb-3"></div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="h-12 bg-neutral-100 rounded"></div>
              <div className="h-12 bg-neutral-100 rounded"></div>
            </div>
            <div className="h-10 bg-neutral-200 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
}
